import { useEffect, useState } from "react";
import { motion, useReducedMotion, type MotionStyle } from "framer-motion";
import { PARALLAX_MEDIUM, useParallaxStyle } from "../lib/parallax";
import type { SiteContent } from "../siteData";

type HeroProps = {
  images: SiteContent["images"];
  meta: SiteContent["meta"];
  imageStyle?: MotionStyle;
  titleStyle?: MotionStyle;
};

export function Hero({ images, meta, imageStyle, titleStyle }: HeroProps) {
  const [hasScrolled, setHasScrolled] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const hideScrollCue = () => {
      if (window.scrollY > 1) {
        setHasScrolled(true);
      }
    };

    hideScrollCue();
    window.addEventListener("scroll", hideScrollCue, { passive: true });
    return () => window.removeEventListener("scroll", hideScrollCue);
  }, []);

  const backgroundStyle = useParallaxStyle({
    output: [0, 0],
    input: [0, 1200],
    disableOnMobile: true,
  });
  const atmosphereStyle = useParallaxStyle({
    output: PARALLAX_MEDIUM.hero.atmosphereY,
    input: [0, 1300],
    disableOnMobile: false,
  });
  const foregroundStyle = useParallaxStyle({
    output: PARALLAX_MEDIUM.hero.foregroundY,
    input: [0, 1000],
    disableOnMobile: true,
  });

  return (
    <header className="hero" aria-label={meta.author}>
      <motion.div
        className="hero__background"
        aria-hidden="true"
        style={{ ...backgroundStyle, ...imageStyle }}
      >
        <img className="hero__image" src={images.hero} alt="" />
      </motion.div>
      <motion.div className="hero__atmosphere" style={atmosphereStyle} aria-hidden="true" />
      <motion.div className="hero__foreground" style={{ ...foregroundStyle, ...titleStyle }}>
        <motion.nav
          className="hero__nav"
          aria-label="Primary navigation"
          initial={prefersReducedMotion ? false : { opacity: 0, y: 18, scale: 0.98 }}
          animate={prefersReducedMotion ? undefined : { opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut", delay: 0.18 }}
        >
          <a className="hero__logoLink" href="/" aria-label={meta.author}>
            <img className="hero__logo" src={images.logo} alt={meta.author} />
          </a>
        </motion.nav>
      </motion.div>
      {!hasScrolled ? (
        <motion.div className="hero__scrollCue" aria-hidden="true">
          <span>Scroll</span>
          <span className="hero__scrollLine" />
        </motion.div>
      ) : null}
    </header>
  );
}
